import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Alert from '../components/Alert';
import { useForm } from '../hooks/useForm';

const ForgotPassword = () => {
    const { t } = useTranslation();
    const { formData, handleChange } = useForm({ email: '' });
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post('/api/auth/forgot-password', { email: formData.email });
            setSubmitted(true);
        } catch (err) {
            setError(err.response?.data?.message || t('auth.forgotPassword.sendError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-transition min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.forgotPassword.title')}</h1>
                    <p className="text-gray-600">
                        {t('auth.forgotPassword.subtitle')}
                    </p>
                </div>

                <div className="card">
                    {submitted ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-semibold mb-2">{t('auth.forgotPassword.successTitle')}</h2>
                            <p className="text-gray-600 mb-6">
                                {t('auth.forgotPassword.sentToPrefix')} <strong>{formData.email}</strong>
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                {t('auth.forgotPassword.checkSpam')}
                            </p>
                            <Link
                                to="/login"
                                className="inline-block text-blue-600 hover:text-blue-700 font-semibold"
                            >
                                {t('auth.forgotPassword.backToLogin')}
                            </Link>
                        </div>
                    ) : (
                        <>
                            <Alert type="error" message={error} onDismiss={() => setError('')} />

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('auth.forgotPassword.email')}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="input-field"
                                        placeholder={t('auth.forgotPassword.emailPlaceholder')}
                                        autoFocus
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.submitButton')}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                                    {t('auth.forgotPassword.backToLogin')}
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
